/**
 * `LedgerSynthetic` — slice **L4** (LR2/LR3 "synthetic edges", running **dark**)
 * of the row-identity ledger design
 * (§3.4, §4 LR2/LR3, §7 row L4). It declares onto the ledger the dependency
 * edges the OLD formula/derived graph does **not** carry — the "external input"
 * one-offs of matrix §2 that L3's shadow (`ledger-shadow.ts`) leaves untouched:
 *
 *  1. **geometry-pref edges** — the render plane's dependency on the calendar
 *     preferences that alter rendered geometry (`firstDay`, `timeFormat`,
 *     `dateFormat`, `secondaryTimezones`, `secondaryCalendars`). Mirrors the
 *     frozen `sheet-invalidation-effects.ts` `RENDER_GEOMETRY_PREFERENCES` →
 *     `CellContentAll` (whole-sheet) routing — one per-sheet render node
 *     `--GEOMETRY-->` each geometry `pref:` node. Faithful to the spec's
 *     representative edge `<render geometry> --GEOMETRY--> pref:firstDay, …`.
 *  2. **reexpand edges** — an alias template's declared `reexpandOn` inputs, the
 *     row-scoped half of preference routing. Each alias instance whose template
 *     declares `reexpandOn: [pathFor(kind)]` gets a `--EXISTENCE-->` edge to
 *     `pref:<kind>` (re-expansion mints/removes children = a structural change).
 *     Mirrors `wireDeclaredReexpand` → `reexpandAliasesByTemplate`. Spec edge
 *     `alias secondary-tz --EXISTENCE--> pref:secondaryTimezones`.
 *  3. **coverage edges** — the per-row fetch plane's dependency on the viewport
 *     `range` node. Each `SourceRow` with a data address gets a `--COVERAGE-->`
 *     edge to `range`. Mirrors the sheet-wide `fetchedRange = null` reset (§5
 *     M5): a viewport move stales every source row's coverage.
 *
 * ## Dual-run, dark by construction (L4 gate = LI3)
 *
 * Nothing here reads the ledger to make a decision. `syncAll` / `declareForRow`
 * only *write* declarations (beside the existing routing, which is unchanged);
 * `checkLi3` and `refereeRouting` only *observe* and count. The frozen
 * `sheet-invalidation-effects.ts` (a sheets-**ui** module this sheets-layer file
 * cannot import anyway) is **mirrored**, never edited — the geometry-pref set
 * below is a deliberate, documented copy.
 *
 * ## Consumer-key namespace (no collision with L3)
 *
 * The synthetic consumers are the **render plane** and the **fetch plane** —
 * genuinely different consumers from L3's data-recompute consumers (§6, the
 * two-plane model). They therefore key in their **own** namespaces
 * (`render:` / `reexpand:` / `coverage:`), disjoint from the `data:<Address>`
 * keys L3's shadow declares. This matters: `Ledger.declareEdges` REPLACES a
 * consumer's ENTIRE edge set, so reusing a row's `data:` key here would clobber
 * its L3 formula/derived edges (a `FormulaDataSource` is both an L3 consumer and
 * a coverage consumer). Distinct keys keep the two declarations independent.
 *
 * ## TDZ constraint (copy of `ledger-shadow.ts`'s rule)
 *
 * This module *value*-imports `RowAddress` (→ `variants/computed-data-source` →
 * `variants/source-row`), the variant classes it `instanceof`-checks, and
 * `alias-templates`. It is therefore only safe to value-import from modules NOT
 * on `../rows/variants/source-row.ts`'s import path — otherwise it closes the eval cycle
 * `source-row → X → row-address → computed-data-source → source-row` and
 * TDZ-crashes on the `extends SourceRow` evaluation (learning
 * `2026-07-09-sheets-module-tdz-cycle-ledger-keys.toml`; js.rows catches it).
 * Only `raw-sheet.ts` value-imports this module (to construct + `syncAll` at
 * hydrate); `alias-templates.ts` reaches the live instance through
 * `sheet.stores.sourceCellData.ledgerSynthetic` **type-only** for the reexpand
 * re-declaration seam. Variant classes MUST NOT import this file.
 */

import type { Option } from "../core/option.ts";
import { DependencyAspect } from "@tento-core/dependency-graph";
import { Ledger } from "@tento-core/ledger";
import { Tracing } from "@tento-core/tracing";

import { RowAddress } from "../rows/row-address.ts";
import { ALIAS_TEMPLATES } from "../aliases/alias-templates.ts";
import type { AliasTemplate } from "../aliases/alias-templates.ts";
import {
  CALENDAR_PREFERENCE_KINDS,
  CalendarPreferencesChanged,
  pathMatches,
  type CalendarPreferenceKind,
} from "../tx/invalidation.ts";
import { LedgerKeys, type SheetLedger } from "../stores/ledger-keys.ts";
import type { ModelSheet } from "../sheet/rows-sheet.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { AliasRow } from "../rows/variants/alias-row.ts";
import { SourceRow } from "../rows/variants/source-row.ts";

/**
 * The calendar preferences whose change alters rendered geometry — a **mirror**
 * of `RENDER_GEOMETRY_PREFERENCES` in the frozen
 * `tento-lona-js/sheets/src/render/frame/sheet-invalidation-effects.ts`. That set lives in
 * the sheets-**ui** layer, which this sheets-layer module cannot import
 * (dependency direction). It is small and stable; the routing suite in
 * `tento-lona-js/tests/invalidation-pipeline` is the drift guard on the
 * upstream copy. Modeled at the `GEOMETRY` facet per the design's representative
 * edge (`<render geometry> --GEOMETRY--> pref:firstDay, …`), NOT `VALUES` — the
 * current whole-sheet `CellContentAll` rebind is the coarse over-approximation
 * the ledger refines.
 */
const GEOMETRY_PREFERENCE_KINDS: readonly CalendarPreferenceKind[] = [
  "firstDay",
  "timeFormat",
  "dateFormat",
  "secondaryTimezones",
  "secondaryCalendars",
];

const RENDER_GEOMETRY_KEY_BODY = "render:geometry";

/**
 * The per-sheet synthetic-edge declarer. Reached by the reexpand seam via
 * `sheet.stores.sourceCellData.ledgerSynthetic`; constructed at hydrate only
 * when a ledger exists (standalone / legacy hydrate has no ledger → the field is
 * `null` and every seam is a `?.` no-op).
 */
export interface LedgerSynthetic {
  /**
   * Hydrate-site bulk declare (LR2/LR3): the sheet's render-geometry edges, plus
   * every alias instance's reexpand edges and every source row's coverage edge.
   * Idempotent — re-running REPLACES each consumer's edge set at the next
   * generation (LI7). Runs the LI3 check + flushes the `ledger/synthetic` breadcrumb.
   */
  syncAll(sheet: ModelSheet): void;

  /**
   * Re-declare a single row's synthetic edges (the row-add / re-expansion
   * re-declaration point). Placement-only / non-participating rows are a no-op.
   * Total — never throws.
   */
  declareForRow(sheet: ModelSheet, row: ModelRow): void;

  /**
   * The re-expansion seam (called from `wireDeclaredReexpand` after
   * `reexpandAliasesByTemplate`): re-declare the reexpanded template's alias
   * instances and their child source rows' coverage edges (new children minted
   * by the re-expansion pick up coverage without waiting for the next hydrate).
   */
  onReexpand(sheet: ModelSheet, templateName: string): void;

  /**
   * **LI3 — pref exhaustiveness** (design §8): after hydrate, every `pref:` node
   * present in the ledger MUST have ≥1 inbound edge. Scans the ledger's nodes,
   * counts holes (a `pref:` node with no consumer — a routing gap), and returns
   * them. Holes are FINDINGS surfaced loud on `ledger/synthetic`, never a throw here.
   */
  checkLi3(): LedgerSynthetic.Li3Result;

  /**
   * The **dual-run referee** (deliverable #4): a one-way containment check of
   * the ledger's declared pref-consumer structure against the routing
   * classification re-encoded here (the exact comparison would require importing
   * the frozen sheets-ui routing table, which this layer cannot reach). See
   * `Referee` for the three assertions.
   */
  refereeRouting(sheet: ModelSheet): LedgerSynthetic.Referee;

  /** Pull-based aggregate counters for tests. */
  snapshot(): LedgerSynthetic.Counters;
}

export namespace LedgerSynthetic {
  /** Aggregate-only observability. */
  export interface Counters {
    /** Consumer declarations written (render + reexpand + coverage). */
    readonly declared: number;
    /** Rows visited that contributed no synthetic edge (placement-only, etc.). */
    readonly skipped: number;
    /** `pref:` nodes with no inbound edge at the last `checkLi3` (should be 0). */
    readonly li3Holes: number;
  }

  export interface Li3Result {
    /** Every `pref:` node present in the ledger. */
    readonly prefNodes: readonly string[];
    /** Of those, the ones with **no** inbound edge (the routing gaps). */
    readonly holes: readonly string[];
  }

  /**
   * The referee's per-check verdict. `ok` is one-way containment (the ledger has
   * at least what the routing demands); a `false` is a divergence surfaced loud.
   */
  export interface Referee {
    /** Every geometry pref reaches the per-sheet render node via a GEOMETRY edge. */
    readonly geometryReachesRender: boolean;
    /** Every kind a present alias reexpands on has ≥1 EXISTENCE consumer. */
    readonly reexpandHasConsumer: boolean;
    /**
     * `defaultWeatherLocation` has **no** GEOMETRY consumer — the negative space
     * the spec calls out (weather ∉ geometry; routing it through the whole-sheet
     * clone IS the unrelated-cell flash). `true` ⟺ the absence holds.
     */
    readonly weatherAbsentFromGeometry: boolean;
  }

  /** Build a synthetic declarer over the sheets-side ledger. */
  export function make(ledger: SheetLedger): LedgerSynthetic {
    return new Impl(ledger);
  }

  /** The render-plane consumer node for a sheet's geometry (one per sheet). */
  export function renderGeometryKey(sheetId: SheetNetworkKey): Ledger.Key {
    return Ledger.key(`${RENDER_GEOMETRY_KEY_BODY}|${sheetId}`);
  }

  /** The reexpand consumer node for an alias instance (placement-keyed). */
  export function reexpandKey(serial: string): Ledger.Key {
    return Ledger.key(`reexpand|${serial}`);
  }

  /** The fetch-plane coverage consumer node for a source row (placement-keyed). */
  export function coverageKey(serial: string): Ledger.Key {
    return LedgerKeys.coverage(serial);
  }

  /**
   * The calendar-preference kinds a template's `reexpandOn` patterns consume —
   * the same classification `wireDeclaredReexpand` + the routing suite use
   * (`pathMatches(pathFor(kind), pattern)`), so declaration and routing read one
   * source. Pure + total.
   */
  export function reexpandKinds(
    template: Option<AliasTemplate>,
  ): CalendarPreferenceKind[] {
    const patterns = template?.reexpandOn ?? [];
    if (patterns.length === 0) return [];
    const out: CalendarPreferenceKind[] = [];
    for (const kind of CALENDAR_PREFERENCE_KINDS) {
      const path = CalendarPreferencesChanged.pathFor(kind);
      if (patterns.some((pattern) => pathMatches(path, pattern))) out.push(kind);
    }
    return out;
  }

  class Impl implements LedgerSynthetic {
    /** Per-consumer monotonic generation (LR5 / LI7 — mirrors L3's map). */
    readonly #generations = new Map<Ledger.Key, number>();

    // Aggregate counters (no per-edge logs).
    #declared = 0;
    #skipped = 0;
    #li3Holes = 0;

    readonly #ledger: SheetLedger;
    constructor(ledger: SheetLedger) {
      this.#ledger = ledger;
    }

    syncAll(sheet: ModelSheet): void {
      this.#declareRenderGeometry(sheet);
      for (const row of sheet.allRows()) this.declareForRow(sheet, row);
      this.#li3Holes = this.checkLi3().holes.length;
      this.#flush();
    }

    declareForRow(sheet: ModelSheet, row: ModelRow): void {
      const before = this.#declared;
      if (row instanceof AliasRow) this.#declareReexpand(sheet, row);
      if (row instanceof SourceRow) this.#declareCoverage(row);
      if (this.#declared === before) this.#skipped += 1;
    }

    onReexpand(sheet: ModelSheet, templateName: string): void {
      for (const row of sheet.allRows()) {
        if (row instanceof AliasRow && row.descriptor.template === templateName) {
          this.#declareReexpand(sheet, row);
        }
        // Re-expansion mints/removes child source rows: refresh their coverage
        // so newly-minted children get a fetch-plane edge without a re-hydrate.
        if (row instanceof SourceRow) this.#declareCoverage(row);
      }
      this.#flush();
    }

    checkLi3(): LedgerSynthetic.Li3Result {
      const prefNodes: string[] = [];
      const holes: string[] = [];
      for (const node of this.#ledger.nodes) {
        if (!node.startsWith("pref:")) continue;
        prefNodes.push(node);
        if (this.#ledger.getDirectDependents(node).size === 0) holes.push(node);
      }
      this.#li3Holes = holes.length;
      return { prefNodes, holes };
    }

    refereeRouting(sheet: ModelSheet): LedgerSynthetic.Referee {
      const renderKey = LedgerSynthetic.renderGeometryKey(sheet.networkKey);

      // (1) every geometry pref must reach the sheet's render node via GEOMETRY.
      let geometryReachesRender = true;
      for (const kind of GEOMETRY_PREFERENCE_KINDS) {
        const consumers = this.#ledger.directConsumers(
          LedgerKeys.pref(kind),
          DependencyAspect.GEOMETRY,
        );
        if (!consumers.has(renderKey)) geometryReachesRender = false;
      }

      // (2) every kind a PRESENT alias reexpands on must have an EXISTENCE
      // consumer (mirror of `wireDeclaredReexpand`'s per-template subscription).
      const reexpandedKinds = new Set<CalendarPreferenceKind>();
      for (const row of sheet.allRows()) {
        if (!(row instanceof AliasRow)) continue;
        for (const kind of LedgerSynthetic.reexpandKinds(this.#templateOf(sheet, row))) {
          reexpandedKinds.add(kind);
        }
      }
      let reexpandHasConsumer = true;
      for (const kind of reexpandedKinds) {
        const consumers = this.#ledger.directConsumers(
          LedgerKeys.pref(kind),
          DependencyAspect.EXISTENCE,
        );
        if (consumers.size === 0) reexpandHasConsumer = false;
      }

      // (3) the negative space: weather-location is NOT a geometry consumer.
      const weatherGeometry = this.#ledger.directConsumers(
        LedgerKeys.pref("defaultWeatherLocation"),
        DependencyAspect.GEOMETRY,
      );

      return {
        geometryReachesRender,
        reexpandHasConsumer,
        weatherAbsentFromGeometry: weatherGeometry.size === 0,
      };
    }

    snapshot(): LedgerSynthetic.Counters {
      return {
        declared: this.#declared,
        skipped: this.#skipped,
        li3Holes: this.#li3Holes,
      };
    }

    // ── Declaration ─────────────────────────────────────────────────────────

    /** Declare the per-sheet render node's GEOMETRY edges to the geometry prefs. */
    #declareRenderGeometry(sheet: ModelSheet): void {
      const consumer = LedgerSynthetic.renderGeometryKey(sheet.networkKey);
      const edges: Ledger.EdgeInput<never>[] = GEOMETRY_PREFERENCE_KINDS.map(
        (kind) => ({
          producer: LedgerKeys.pref(kind),
          facets: [DependencyAspect.GEOMETRY],
          scope: null,
        }),
      );
      this.#declare(consumer, edges);
    }

    /** Declare an alias instance's EXISTENCE edges to the prefs it reexpands on. */
    #declareReexpand(sheet: ModelSheet, alias: AliasRow): void {
      const kinds = LedgerSynthetic.reexpandKinds(this.#templateOf(sheet, alias));
      if (kinds.length === 0) return; // no declared inputs → not a consumer
      const consumer = LedgerSynthetic.reexpandKey(alias.id.serial);
      const edges: Ledger.EdgeInput<never>[] = kinds.map((kind) => ({
        producer: LedgerKeys.pref(kind),
        facets: [DependencyAspect.EXISTENCE],
        scope: null,
      }));
      this.#declare(consumer, edges);
    }

    /** Declare a source row's COVERAGE edge to the viewport `range` node. */
    #declareCoverage(row: SourceRow): void {
      // Only rows with a data stream have a fetch plane (placement-only rows own
      // no data — the null-reset never touched them either).
      if (RowAddress.of(row).kind !== "data") return;
      const consumer = LedgerSynthetic.coverageKey(row.id.serial);
      this.#declare(consumer, [
        {
          producer: LedgerKeys.RANGE,
          facets: [DependencyAspect.COVERAGE],
          scope: null,
        },
      ]);
    }

    /** Replace `consumer`'s edge set at the next generation (LR5 / LI7). */
    #declare(
      consumer: Ledger.Key,
      edges: readonly Ledger.EdgeInput<never>[],
    ): void {
      const generation = (this.#generations.get(consumer) ?? 0) + 1;
      this.#generations.set(consumer, generation);
      this.#ledger.declareEdges(consumer, generation, edges);
      this.#declared += 1;
    }

    /** Resolve an alias's template (sheet overlay first, then the sheet's registries). */
    #templateOf(sheet: ModelSheet, alias: AliasRow): Option<AliasTemplate> {
      const name = alias.descriptor.template;
      return sheet.aliasTemplateOverlay(name) ?? sheet.registries.aliasTemplates.get(name);
    }

    // `ledger/synthetic`: synthetic-edge declarations, skipped rows, and LI3
    // holes; one Tracing.log per sync batch (never per edge).
    #flush(): void {
      Tracing.log("ledger/synthetic", {
        declared: this.#declared,
        skipped: this.#skipped,
        li3Holes: this.#li3Holes,
      });
    }
  }
}
